import React, { useCallback, useEffect, useMemo, useState } from 'react'
import B2BLayout from '../../../layouts/b2b'
import {
  Button,
  Switch,
  Form,
  Popconfirm,
  Select,
  Table,
  Tooltip,
  InputNumber,
  Input,
  message,
  Tabs,
  Space,
  Spin,
} from 'antd'
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
  StopOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import localEnums from '../fields/enums.json'
import { useFieldGroups, FieldGroup, FieldGroupWithFields } from './hooks/useFieldGroups'
import { SortableItemsSelector } from '../../../components/sortable-items-selector'

interface CardFieldsData {
  [cardType: string]: {
    [fieldName: string]: string
  }
}

const { Option } = Select

const requiredFields = ['name', 'card_type']

const FieldGroupsPage = () => {
  const pageTitle = useMemo(() => 'Группы полей', [])

  const [form] = Form.useForm()
  const [data, setData] = useState<FieldGroup[]>([])
  const [editingKey, setEditingKey] = useState<null | number>(null)
  const [sorter, setSorter] = useState<any>({})
  const navigate = useNavigate()
  const [pagination, setPagination] = useState<any>({
    current: 1,
    pageSize: 20,
    position: ['bottomCenter'],
  })

  const urlParams = useMemo(() => Object.fromEntries(new URLSearchParams(location.search)), [])
  const [currentFilters, setCurrentFilters] = useState<any>({
    card_type: urlParams?.card_type || 'order',
    is_active: urlParams?.is_active,
  })

  const [selectedCardType, setSelectedCardType] = useState<string>(urlParams?.card_type || 'order')
  const [isFieldsModalVisible, setIsFieldsModalVisible] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<FieldGroupWithFields | null>(null)
  const [cardFieldsData, setCardFieldsData] = useState<CardFieldsData | null>(null)
  const [cardTypes, setCardTypes] = useState<Record<string, string>>(localEnums.card_type)
  const [loadingFields, setLoadingFields] = useState(false)
  const [usedFields, setUsedFields] = useState<string[]>([])

  const {
    loading,
    error,
    clearError,
    getFieldGroups,
    createFieldGroup,
    updateFieldGroup,
    deleteFieldGroup,
    getFieldGroupWithFields,
    assignFieldsToGroup,
    updateSortOrder,
    getCardFields,
    getFieldsUsedByOtherGroups,
  } = useFieldGroups()

  const isEditing = (record: FieldGroup) => record.id === editingKey

  const loadData = useCallback(async () => {
    const params = {
      card_type: selectedCardType,
      page: pagination.current,
      limit: pagination.pageSize,
      ...currentFilters,
    }

    const result = await getFieldGroups(params)
    if (result) {
      setData(result.data || [])
      setPagination(prev => ({
        ...prev,
        total: result.total,
      }))
    }
  }, [getFieldGroups, selectedCardType, pagination.current, pagination.pageSize, currentFilters])

  useEffect(() => {
    loadData()
  }, [loadData])

  useEffect(() => {
    if (error) {
      message.error(error)
      clearError()
    }
  }, [error, clearError])

  // Загрузка полей для карточек с бэкенда
  useEffect(() => {
    const fetchCardFields = async () => {
      setLoadingFields(true)
      try {
        const metadata = await getCardFields()
        if (metadata) {
          setCardFieldsData(metadata.fields)
          setCardTypes(metadata.cardTypes || localEnums.card_type)
        }
      } catch (err) {
        console.error('Ошибка загрузки полей:', err)
        message.error('Не удалось загрузить поля карточек')
      } finally {
        setLoadingFields(false)
      }
    }

    fetchCardFields()
  }, [getCardFields])

  const addRow = useCallback(() => {
    const newRecord = {
      id: Date.now(),
      name: '',
      card_type: selectedCardType,
      sort_order: data.length,
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }
    setData([...data, newRecord])
    setEditingKey(newRecord.id)
    form.setFieldsValue(newRecord)
  }, [data, selectedCardType, form])

  const cancel = useCallback(() => {
    setEditingKey(null)
    form.resetFields()
    loadData()
  }, [form, loadData])

  const save = useCallback(
    async (id: number) => {
      try {
        const row = await form.validateFields()
        const isNew = id > 1000000000 // Временные ID больше этого значения

        if (isNew) {
          const result = await createFieldGroup({
            name: row.name,
            card_type: selectedCardType,
            sort_order: row.sort_order || 0,
            is_active: row.is_active !== undefined ? row.is_active : true,
          })
          if (result) {
            message.success('Группа создана успешно')
          }
        } else {
          const result = await updateFieldGroup(id, {
            name: row.name,
            sort_order: row.sort_order,
            is_active: row.is_active,
          })
          if (result) {
            message.success('Группа обновлена успешно')
          }
        }

        setEditingKey(null)
        form.resetFields()
        loadData()
      } catch (errInfo) {
        console.error('Validate Failed:', errInfo)
      }
    },
    [form, createFieldGroup, updateFieldGroup, selectedCardType, loadData],
  )

  const handleDelete = useCallback(
    async (id: number) => {
      const result = await deleteFieldGroup(id)
      console.log('result', result)
      if (result) {
        message.success('Группа удалена успешно')
        loadData()
      }
    },
    [deleteFieldGroup, loadData],
  )

  const handleCardTypeChange = useCallback(
    (cardType: string) => {
      setSelectedCardType(cardType)
      setCurrentFilters(prev => ({ ...prev, card_type: cardType }))
      navigate(`/admin/field-groups?card_type=${cardType}`, { replace: true })
    },
    [navigate],
  )

  const openFieldsModal = useCallback(
    async (group: FieldGroup) => {
      setLoadingFields(true)
      try {
        const [groupWithFields, usedFieldsResult] = await Promise.all([
          getFieldGroupWithFields(group.id),
          getFieldsUsedByOtherGroups(selectedCardType, group.id),
        ])

        if (groupWithFields) {
          setSelectedGroup(groupWithFields as FieldGroupWithFields)
          setUsedFields(usedFieldsResult?.fields || [])
          setIsFieldsModalVisible(true)
        }
      } catch (err) {
        console.error('Ошибка загрузки данных:', err)
        message.error('Не удалось загрузить данные')
      } finally {
        setLoadingFields(false)
      }
    },
    [getFieldGroupWithFields, getFieldsUsedByOtherGroups, selectedCardType],
  )

  const handleFieldsModalOk = useCallback(
    async (selectedFieldKeys: string[]) => {
      if (!selectedGroup) return

      const fields = selectedFieldKeys.map((fieldName, index) => ({
        field_name: fieldName,
        sort_order: index,
      }))

      const result = await assignFieldsToGroup(selectedGroup.id, { fields })
      if (result) {
        message.success('Поля назначены в группу успешно')
        setIsFieldsModalVisible(false)
        setSelectedGroup(null)
        loadData()
      }
    },
    [selectedGroup, assignFieldsToGroup, loadData],
  )

  const getAvailableFields = useCallback(() => {
    const cardFields =
      cardFieldsData?.[selectedCardType] || localEnums.fields[selectedCardType] || {}
    return Object.keys(cardFields)
      .filter(key => !usedFields.includes(key))
      .map(key => ({
        key,
        title: cardFields[key] || key,
        description: key,
      }))
      .filter(field => field.title && field.title.trim() !== '')
  }, [selectedCardType, cardFieldsData, usedFields])

  const EditableCell = ({ editing, dataIndex, title, children, ...restProps }) => {
    const getInputNode = () => {
      switch (dataIndex) {
        case 'name':
          return <Input />
        case 'sort_order':
          return <InputNumber min={0} />
        case 'is_active':
          return <Switch />
        default:
          return <Input />
      }
    }

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={
              requiredFields.includes(dataIndex)
                ? [{ required: true, message: `${title} обязательно` }]
                : []
            }
          >
            {getInputNode()}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    )
  }

  const columns = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      editable: true,
      sorter: true,
      width: '40%',
    },
    {
      title: 'Сортировка',
      dataIndex: 'sort_order',
      key: 'sort_order',
      editable: true,
      sorter: true,
      width: '5%',
    },
    {
      title: 'Активна',
      dataIndex: 'is_active',
      key: 'is_active',
      editable: true,
      width: '5%',
      render: (value: boolean) => <Switch checked={value} disabled />,
    },
    {
      title: 'Действия',
      dataIndex: 'operation',
      width: '5%',
      render: (_, record: FieldGroup) => {
        const editable = isEditing(record)
        return editable ? (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Tooltip title="Сохранить">
              <Button
                type="primary"
                icon={<SaveOutlined style={{ fontSize: '16px' }} />}
                onClick={() => save(record.id)}
              />
            </Tooltip>
            <Tooltip title="Отменить">
              <Button icon={<StopOutlined style={{ fontSize: '16px' }} />} onClick={cancel} />
            </Tooltip>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Tooltip title="Редактировать">
              <Button
                icon={<EditOutlined style={{ fontSize: '16px' }} />}
                disabled={editingKey !== null}
                onClick={() => {
                  setEditingKey(record.id)
                  form.setFieldsValue(record)
                }}
              />
            </Tooltip>
            <Tooltip title="Настроить поля">
              <Button
                icon={<SettingOutlined style={{ fontSize: '16px' }} />}
                disabled={editingKey !== null}
                onClick={() => openFieldsModal(record)}
              />
            </Tooltip>
            <Popconfirm
              title="Уверены, что хотите удалить?"
              onConfirm={() => handleDelete(record.id)}
              disabled={editingKey !== null}
            >
              <Tooltip title="Удалить">
                <Button
                  danger
                  icon={<DeleteOutlined style={{ fontSize: '16px' }} />}
                  disabled={editingKey !== null}
                />
              </Tooltip>
            </Popconfirm>
          </div>
        )
      },
    },
  ]

  const mergedColumns = columns.map(col => {
    if (!col.editable) {
      return col
    }
    return {
      ...col,
      onCell: (record: FieldGroup) => ({
        record,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    }
  })

  return (
    <B2BLayout pageTitle={pageTitle} isViewScrollButton>
      <Space
        style={{ width: '100%', justifyContent: 'space-between', paddingBottom: 10 }}
        align={'start'}
        wrap
      >
        <Select value={selectedCardType} onChange={handleCardTypeChange} style={{ width: 200 }}>
          {Object.keys(cardTypes).map(key => (
            <Option key={key} value={key}>
              {cardTypes[key]}
            </Option>
          ))}
        </Select>
        <Button onClick={addRow} disabled={editingKey !== null}>
          <PlusOutlined /> Добавить группу
        </Button>
      </Space>

      <Form form={form} component={false}>
        <Table
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          bordered
          dataSource={data}
          columns={mergedColumns}
          rowClassName="editable-row"
          pagination={{
            ...pagination,
            showSizeChanger: true,
          }}
          loading={loading}
          onChange={(paginationInfo, filters, sorterInfo) => {
            setPagination(paginationInfo)
            setSorter(sorterInfo)
          }}
          rowKey="id"
          size="small"
        />
      </Form>

      <SortableItemsSelector
        visible={isFieldsModalVisible}
        title={`Настройка полей для группы "${selectedGroup?.name}"`}
        availableItems={getAvailableFields()}
        selectedItemKeys={
          selectedGroup?.fields
            ?.sort((a, b) => a.sort_order - b.sort_order)
            .map(f => f.field_name) || []
        }
        loading={loadingFields}
        leftPanelTitle="Доступные поля"
        rightPanelTitle="Поля в группе"
        emptyLeftText="Нет доступных полей"
        emptyRightText="Добавьте поля из списка слева"
        emptyRightDescription="Нажмите на поле или используйте кнопку +"
        width={1000}
        onOk={handleFieldsModalOk}
        onCancel={() => {
          setIsFieldsModalVisible(false)
          setSelectedGroup(null)
        }}
      />
    </B2BLayout>
  )
}

export default FieldGroupsPage
